// Perzistence UI layoutu do URL fragmentu (`#…`). Fragment se neposílá na server.
//   - BOOT: jednou po prvním renderu s vrstvami přečte hash a aplikuje rect/mode (poziční dle
//     pořadí navigačních vrstev) + stackOrigin do WM storu.
//   - ZÁPIS: debounced po ustálení změn geometrie/struktury/originu přes history.replaceState
//     (žádný popstate → kernel se nedotkne; router preservuje hash, viz routerAdapter).
// Mountuje se v PhoenixDesktop (před seedovacím efektem, aby kotva měla přednost před fallbackem).

import { useEffect, useMemo, useRef } from 'react';

import { useNavigationStore } from '@/shared/navigation/react/useNavigationContext';

import { useWindowManagerStore } from '../core/windowManagerStore';
import type { WindowRect } from '../core/windowManagerTypes';
import { decodeLayout, encodeLayout, rectFromEntry } from '../core/windowLayoutCodec';

const WRITE_DEBOUNCE_MS = 250;

export function useWindowLayoutUrlHash(): void {
  const navigation = useNavigationStore((s) => s.navigation);
  const windows = useWindowManagerStore((s) => s.windows);
  const stackOrigin = useWindowManagerStore((s) => s.stackOrigin);
  const setRects = useWindowManagerStore((s) => s.setRects);
  const setMode = useWindowManagerStore((s) => s.setMode);
  const setStackOrigin = useWindowManagerStore((s) => s.setStackOrigin);

  const layerIds = useMemo(() => navigation.layers.map((l) => l.layerId), [navigation.layers]);
  // Aktuálně existující stackId (floating skupiny) — pro ořez stackOrigin v kotvě.
  const activeStackIds = useMemo(
    () =>
      new Set(
        navigation.layers.map((l) => l.stackId).filter((s): s is string => typeof s === 'string')
      ),
    [navigation.layers]
  );
  const appliedRef = useRef(false);

  // BOOT: jednou, jakmile jsou vrstvy k dispozici (po restoreFromCurrentUrl). Poziční mapování
  // entry.i → layerIds[i] (pořadí vrstev round-tripuje přes query).
  useEffect(() => {
    if (appliedRef.current || layerIds.length === 0) return;
    appliedRef.current = true;

    const snap = decodeLayout(window.location.hash.replace(/^#/, ''));
    if (!snap) return;

    const rects: Array<[string, WindowRect]> = [];
    for (const entry of snap.w) {
      const id = layerIds[entry.i];
      if (!id) continue;
      if (entry.r) rects.push([id, rectFromEntry(entry.r)]);
      if (entry.m) setMode(id, entry.m);
    }
    if (rects.length > 0) setRects(rects);
    for (const [sid, origin] of Object.entries(snap.o ?? {})) {
      if (typeof origin === 'string') setStackOrigin(sid, origin);
    }
  }, [layerIds, setRects, setMode, setStackOrigin]);

  // ZÁPIS: debounced. Až po boot-apply (jinak bychom přepsali kotvu před obnovou).
  useEffect(() => {
    if (!appliedRef.current) return;
    const t = window.setTimeout(() => {
      // stackOrigin ořežeme jen na aktuálně existující stacky — staré (po dock/zavření) záznamy
      // do kotvy nepatří (jinak v `o` zůstávají stackId, co neodpovídají žádnému `sid`).
      const prunedOrigin: Record<string, string> = {};
      for (const [k, v] of Object.entries(stackOrigin)) {
        if (activeStackIds.has(k)) prunedOrigin[k] = v;
      }
      const enc = encodeLayout(layerIds, windows, prunedOrigin);
      const { pathname, search } = window.location;
      const url = enc ? `${pathname}${search}#${enc}` : `${pathname}${search}`;
      try {
        window.history.replaceState(window.history.state, '', url);
      } catch {
        /* replaceState nedostupné — ignorujeme */
      }
    }, WRITE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [layerIds, windows, stackOrigin, activeStackIds]);
}
